import $ from 'jquery'
import isNil from 'lodash/isNil'
import OLGeomLineString from 'ol/geom/LineString'
import OLGeomPolygon from 'ol/geom/Polygon'
import OLInteractionDoubleClickZoom from 'ol/interaction/DoubleClickZoom'
import OLInteractionDraw from 'ol/interaction/Draw'
import OLLayerVector from 'ol/layer/Vector'
import { unByKey as OLObservableUnByKey } from 'ol/Observable'
import OLOverlay from 'ol/Overlay'
import OLSourceVector from 'ol/source/Vector'
import { getArea, getLength } from 'ol/sphere'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import PoziMap from 'map/map'
import { formatArea, formatLength } from 'utils/measurement'
import { showSnackbar } from 'utils/snackbar/actions'

/**
 * Pozi Measure module.
 * Also hooks and minor display directives for multi-select as this is very similar select function.
 */
export default function PoziMeasure () {
  // Adapters
  const adapters = {
    start: function (options) {
      return start(options)
    },
    clear: function () {
      return clear()
    }
  }

  const app = PoziUtils.getApp()

  let bInProgress = false

  /**
   * Type of measurement
   * @type {string}
   */
  let measureType = 'area'

  /**
   * Currently drawn feature.
   * @type {ol.Feature}
   */
  let sketch

  /**
   * The help tooltip element.
   * @type {Element}
   */
  let helpTooltipElement

  /**
   * Overlay to show the help messages.
   * @type {ol.Overlay}
   */
  let helpTooltip

  /**
   * The measure tooltip element.
   * @type {Element}
   */
  let measureTooltipElement

  /**
   * Overlay to show the measurement.
   * @type {ol.Overlay}
   */
  let measureTooltip

  /**
   * Message to show when the user is drawing a polygon.
   * @type {string}
   */
  const continuePolygonMsg = 'Click to continue drawing the polygon <br/>Double-click to finish'

  /**
   * Message to show when the user is drawing a line.
   * @type {string}
   */
  const continueLineMsg = 'Click to continue drawing the line <br/>Double-click to finish'

  // var typeSelect = document.getElementById('type')

  let draw

  let listenerMouseOut

  let listenerPointerMove

  let listener

  // var interactions

  let bShowTooltip = true

  let callback

  let style = {
    'circle-fill-color': '#ffcc33',
    'circle-radius': 7,
    'fill-color': 'rgba(255, 255, 255, 0.2)',
    'stroke-color': 'green',
    'stroke-width': 2
  }

  let styleDraw = {
    'circle-fill-color': 'rgba(255, 255, 255, 0.2)',
    'circle-radius': 5,
    'circle-stroke-color': 'rgba(0, 0, 0, 0.7)',
    'fill-color': 'rgba(255, 255, 255, 0.2)',
    'fill-line-dash': [10, 10],
    'stroke-color': 'rgba(0, 0, 0, 0.4)',
    'stroke-line-dash': [10, 10],
    'stroke-width': 5
  }

  // ============================================================
  // Public Functions
  // ============================================================

  var getOlLayer = function () {
    return Model.get('app.measure.ollayer', null)
  }

  var _end = function () {
    if (!bInProgress) {
      return
    }
    bInProgress = false

    if (measureTooltipElement) {
      measureTooltipElement.parentNode.removeChild(measureTooltipElement)
    }

    sketch = null // unset sketch
    measureTooltipElement = null // unset tooltip so that a new one can be created
    if (listener) {
      OLObservableUnByKey(listener)
    }
    helpTooltipElement.classList.add('hidden')
    if (listenerPointerMove) {
      // app.map.OLObservableUnByKey(listenerPointerMove)
      OLObservableUnByKey(listenerPointerMove)
      listenerPointerMove = null
    }
    if (listenerMouseOut) {
      app.map.getViewport().removeEventListener('mouseout', listenerMouseOut)
      listenerMouseOut = null
    }
    if (draw) {
      app.map.removeInteraction(draw)
      draw = null
    }
    interactionsOn(true)
    clear()

    Model.set('app.measure.active', false)
  }

  /**
   * Start the measure tool
   * @param {string} mt measureType area|distance.
   * @param {function} cb callback
   */
  var start = function (options) {
    _end()

    // See settimeout on _end interactions... this is simply to avoid event propogation of double click.
    setTimeout(function () {
      measureType = options.measuretype
      callback = options.callback
      style = options.style
      styleDraw = options.styleDraw
      bShowTooltip = options.showtooltip

      clear()

      let olLayer = Model.get('app.measure.ollayer')

      if (!olLayer) {
        const sourceVector = new OLSourceVector()

        olLayer = new OLLayerVector({
          map: app.map,
          source: sourceVector,
          style
        })

        interactionsOn(false)

        addInteraction(sourceVector)
        Model.set('app.measure.ollayer', olLayer)
      }

      Model.set('app.measure.active', true)

      window.store.dispatch(showSnackbar({ message: 'Select points on the map. Double click to end.' }))
    }, 0)
  }

  /**
   * Clear any old markup
   */
  var clear = function () {
    console.debug('Measure - clear')
    $('.tooltip.tooltip-static').hide()
    const olLayer = getOlLayer()
    if (isNil(olLayer)) {
      console.log('Measure tool, no current measure layer found.')
      return
    }

    // Fading out very quickly as removeLayer does not seem to work
    PoziMap().fadeOutLayer(olLayer, 10)

    Model.unset('app.measure.ollayer')
  }

  // ============================================================
  // Private Functions
  // ============================================================

  /**
   * Enable/Disable double click to zoom
   * @param {boolean} activate true|false.
   */
  var interactionsOn = function (activate) {
    if (activate) {
      // Not very happy about this timeout, however, unable to figure out how to stop event propagation when reenabling the default click behaviour.
      setTimeout(function () {
        console.log('Measure: Adding map interactions')
        app.map.addInteraction(new OLInteractionDoubleClickZoom())
        PoziMap().enableDefaultMapClickInteration()
      }, 0)
    } else {
      PoziMap().disableDefaultMapClickInteration()
      const interactions = app.map.getInteractions()
      for (let i = 0; i < interactions.getLength(); i++) {
        const interaction = interactions.item(i)
        if (interaction instanceof OLInteractionDoubleClickZoom) {
          console.log('Measure: Remove map interactions...')
          app.map.removeInteraction(interaction)
        }
      }
    }
  }

  /**
   * Handle pointer move.
   * @param {ol.MapBrowserEvent} evt The event.
   */
  const pointerMoveHandler = function (evt) {
    if (evt.dragging) {
      return
    }
    /** @type {string} */
    let helpMsg = 'Click to start drawing'

    if (sketch) {
      const geom = sketch.getGeometry()
      if (geom instanceof OLGeomPolygon) {
        helpMsg = continuePolygonMsg
      } else if (geom instanceof OLGeomLineString) {
        helpMsg = continueLineMsg
      }
    }

    if (isNil(helpTooltipElement)) {
      console.log('pointerMoveHandler failed to get helpTooltipElement.')
      return
    }

    helpTooltipElement.innerHTML = helpMsg
    helpTooltip.setPosition(evt.coordinate)
    helpTooltipElement.classList.remove('hidden')
  }

  var addInteraction = function (sourceVector) {
    const type = measureType === 'area' ? 'Polygon' : 'LineString'
    draw = new OLInteractionDraw({
      source: sourceVector,
      type,
      style: styleDraw
    })
    app.map.addInteraction(draw)

    addTooltipInteraction()
  }

  var addTooltipInteraction = function () {
    createMeasureTooltip()
    createHelpTooltip()

    draw.on(
      'drawstart',
      function (evt) {
        bInProgress = true
        // Not sure why we had this. Based on customer feedback, leave existing feature overlays.
        // https://trello.com/c/xfEHITCh/1884-community-map-horsham
        // app.poziMap.removeAllOverlayFeatures()

        // set sketch
        sketch = evt.feature

        /** @type {ol.Coordinate|undefined} */
        let tooltipCoord = evt.coordinate

        listener = sketch.getGeometry().on('change', function (evt) {
          const geom = evt.target
          let output
          if (geom instanceof OLGeomPolygon) {
            const area = getArea(geom) // in m2
            output = formatArea(area)
            tooltipCoord = geom.getInteriorPoint().getCoordinates()
          } else if (geom instanceof OLGeomLineString) {
            const length = getLength(geom)
            output = formatLength(length)
            tooltipCoord = geom.getLastCoordinate()
          }
          // Do not add tooltips for polygonSelectMode
          if (bShowTooltip) {
            if (isNil(measureTooltipElement)) {
              console.log('pointerMoveHandler failed to get measureTooltipElement.')
              return
            }
            measureTooltipElement.innerHTML = output
            measureTooltip.setPosition(tooltipCoord)
          }
        })
      },
      this
    )

    draw.on(
      'drawend',
      function (evt) {
        // Attempt to stop reenabling click from triggering zoom... didn't work. Had to use setTimeout 0
        // TODO: try return false
        evt.stopPropagation()
        evt.preventDefault()

        bInProgress = false
        if (isNil(measureTooltipElement)) {
          console.log('measureTooltipElement invalid!')
          return
        }
        measureTooltipElement.className = 'tooltip tooltip-static'
        measureTooltip.setOffset([0, -7])
        sketch = null // unset sketch
        measureTooltipElement = null // unset tooltip so that a new one can be created
        createMeasureTooltip()
        OLObservableUnByKey(listener)

        helpTooltipElement.classList.add('hidden')
        if (listenerPointerMove) {
          // app.map.OLObservableUnByKey(listenerPointerMove)
          OLObservableUnByKey(listenerPointerMove)
          listenerPointerMove = null
        }
        if (listenerMouseOut) {
          console.log('Measure: Remove mouseout listener')
          app.map.getViewport().removeEventListener('mouseout', listenerMouseOut)
          listenerMouseOut = null
        }
        if (draw) {
          app.map.removeInteraction(draw)
          draw = null
        }
        interactionsOn(true)

        if (callback) {
          callback(evt)
        }

        Model.set('app.measure.active', false)

        return true
      },
      this
    )

    listenerPointerMove = app.map.on('pointermove', pointerMoveHandler)
    listenerMouseOut = app.map.getViewport().addEventListener('mouseout', function () {
      helpTooltipElement.classList.add('hidden')
    })
  }

  /**
   * Creates a new help tooltip
   */
  var createHelpTooltip = function () {
    helpTooltipElement = document.createElement('div')
    if (isNil(helpTooltipElement)) {
      console.log('helpTooltipElement invalid!')
      return
    }
    helpTooltipElement.className = 'tooltip hidden'
    helpTooltip = new OLOverlay({
      element: helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left'
    })
    app.map.addOverlay(helpTooltip)
  }

  /**
   * Creates a new measure tooltip
   */
  var createMeasureTooltip = function () {
    if (measureTooltipElement) {
      measureTooltipElement.parentNode.removeChild(measureTooltipElement)
    }
    measureTooltipElement = document.createElement('div')
    if (isNil(measureTooltipElement)) {
      console.log('measureTooltipElement invalid!')
      return
    }
    measureTooltipElement.className = 'tooltip tooltip-measure'
    measureTooltip = new OLOverlay({
      element: measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center'
    })
    app.map.addOverlay(measureTooltip)
  }

  // Return adapters (must be at end of adapter)
  return adapters
}
